import React from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';

const SUMMARY_ITEMS = [
  {
    icon: '/assets/img/panda/pricing/chef_fee.svg',
    label: 'Chefs',
    value: '30% platform fee',
  },
  {
    icon: '/assets/img/panda/pricing/customer_order.svg',
    label: 'Customers',
    value: 'Tax applies to customer order',
  },
  {
    icon: '/assets/img/panda/pricing/distance_delivery.svg',
    label: 'Delivery',
    value: 'Fee based on distance',
  },
];

const ORDER_TYPES = [
  {
    title: 'Customer Orders',
    subtitle: 'For individual and team meal orders',
    points: [
      "Food is priced by the selected restaurant/chef",
      "Applicable tax is added to the customer's order",
      'Delivery fee is calculated from delivery distance',
      'No separate chef-side fee is charged to the customer',
    ],
    cta: { label: 'Start an Order', to: '/browse-restaurants' },
    primary: true,
  },
  {
    title: 'Corporate Catering',
    subtitle: 'For offices, recurring and larger orders',
    points: [
      'Custom menus and recurring meal plans',
      'Flexible order and delivery scheduling',
      'Custom pricing based on order volume',
      'Contact our team for a tailored quote',
    ],
    cta: { label: 'Contact Sales', to: '/contact' },
    primary: false,
  },
];

const FEE_BREAKDOWN = [
  {
    icon: '/assets/img/panda/pricing/customer_order.svg',
    title: 'Customer order',
    description:
      "Tax is applied to the customer's order according to the applicable tax setup.",
  },
  {
    icon: '/assets/img/panda/pricing/distance_delivery.svg',
    title: 'Delivery',
    description:
      'Delivery fee is calculated according to the delivery distance.',
  },
  {
    icon: '/assets/img/panda/pricing/chef_fee.svg',
    title: 'Chef / restaurant',
    description: 'A 30% platform fee applies to chef/restaurant orders.',
  },
  {
    icon: '/assets/img/panda/pricing/corporate.svg',
    title: 'Corporate',
    description:
      'Corporate pricing is customized based on order volume and requirements.',
  },
];

const Pricing = () => {
  return (
    <main className="bg-[#FDF6EE] pt-[88px]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-8 text-center">
        <p className="text-[11px] sm:text-xs font-bold text-[#E53935] uppercase tracking-wider mb-2">
          Simple, Transparent Pricing
        </p>
        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#202124] leading-tight">
          Pricing built around how you order.
        </h1>
        <p className="mt-4 text-gray-500 text-sm sm:text-base max-w-xl mx-auto">
          Clear platform fees. Customer taxes only where applicable.
          Delivery fees are calculated based on delivery distance.
        </p>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-14">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {SUMMARY_ITEMS.map(item => (
            <div
              key={item.label}
              className="bg-white rounded-2xl border border-gray-200/80 p-5 flex items-center gap-4"
            >
              <img src={item.icon} alt="" className="w-12 h-12 flex-shrink-0" />
              <span>
                <span className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                  {item.label}
                </span>
                <span className="block font-heading font-bold text-sm text-[#202124] mt-0.5">
                  {item.value}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-16">
        <div className="text-center mb-8">
          <p className="text-[11px] sm:text-xs font-bold text-[#E53935] uppercase tracking-wider mb-2">
            Our Pricing
          </p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#202124]">
            Choose the way your team orders.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {ORDER_TYPES.map(order => (
            <div
              key={order.title}
              className={`rounded-2xl p-6 sm:p-8 flex flex-col ${
                order.primary
                  ? 'bg-[#202124] text-white'
                  : 'bg-white border border-gray-200/80 text-[#202124]'
              }`}
            >
              <h3 className="font-heading font-bold text-xl mb-1">
                {order.title}
              </h3>
              <p
                className={`text-sm mb-6 ${
                  order.primary ? 'text-white/70' : 'text-gray-500'
                }`}
              >
                {order.subtitle}
              </p>
              <ul className="space-y-3 mb-8 flex-1">
                {order.points.map(point => (
                  <li key={point} className="flex items-start gap-2.5 text-sm">
                    <Check
                      className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                        order.primary ? 'text-[#FCA5A5]' : 'text-[#E53935]'
                      }`}
                    />
                    <span className={order.primary ? 'text-white/90' : 'text-gray-600'}>
                      {point}
                    </span>
                  </li>
                ))}
              </ul>
              <Link
                to={order.cta.to}
                className={`inline-flex justify-center items-center px-5 py-3 rounded-xl text-sm font-heading font-bold transition-colors ${
                  order.primary
                    ? 'bg-[#E53935] text-white hover:bg-[#d32f2f]'
                    : 'border border-gray-200 text-[#202124] hover:border-[#E53935] hover:text-[#E53935]'
                }`}
              >
                {order.cta.label}
              </Link>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border-t border-gray-200/80">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
          <p className="text-[11px] sm:text-xs font-bold text-[#E53935] uppercase tracking-wider mb-3 text-center">
            For Chefs &amp; Restaurant Partners
          </p>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-[#202124] text-center mb-4">
            30% platform fee
          </h2>
          <p className="text-sm sm:text-base text-gray-500 max-w-xl mx-auto text-center mb-12">
            Panda On Wheels retains 30% of the applicable order value as the
            chef/restaurant platform fee. Customer tax and distance-based
            delivery are handled separately.
          </p>

          <h3 className="font-heading font-bold text-base text-[#202124] mb-5 text-center">
            How fees work
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEE_BREAKDOWN.map(item => (
              <div
                key={item.title}
                className="bg-panda-tint/20 rounded-2xl p-5"
              >
                <img src={item.icon} alt="" className="w-10 h-10 mb-3" />
                <p className="font-heading font-bold text-sm text-[#202124] mb-1.5">
                  {item.title}
                </p>
                <p className="text-xs text-gray-500 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
};

export default Pricing;
